/* Built In Imports */
import { useContext, useEffect } from 'react';

/* External Imports */
import { Box, ChakraProvider } from '@chakra-ui/react';
import { nanoid } from 'nanoid';

/* Internal Imports */
/* Components */
import config from '@config';
import PageConfig from '@lb/Common/PageConfig';
import RenderSections from '@lb/Common/RenderSections';
import WrapLayout from '@lb/components/Layout/WrapLayout';
import Breadcrum from '@lb/components/Navigation/Breadcrum';
import HeadComponent from '@lb/components/Utility/HeadComponent';
import { AuthContext } from '@store/auth-context';

/* Services */
import { getLocale } from '@msr/components/Utility/Shared/SharedService';
import {
    fetchFooterDetailData,
    fetchHeaderMenuData,
} from 'services/commonService';
import { fetchDetails, getLandingPaths } from 'services/lbService';

/* Styles */
import { lingaBhairaviTheme } from '@styles/theme';
/**
 *
 * @param {object} pageData
 * @param lang
 * @param region
 * @returns
 */

const Events = ({ pageData, region, lang }) => {
  const authContext = useContext(AuthContext);

  useEffect(() => {
    authContext.RegionAndLang(region, lang);
  }, [authContext, region, lang]);

  return (
    <WrapLayout pageData={pageData}>
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData.availableLocales}
      >
        {' '}
      </HeadComponent>
      {pageData?.breadcrumbs?.length > 0 && (
        <Breadcrum
          page={pageData.breadcrumbs}
          alsoin={pageData.alsoin}
          url=""
          region={region}
          lang={lang}
        />
      )}
      <ChakraProvider resetCSS theme={lingaBhairaviTheme}>
        <Box
          as="section"
          className="container-article"
          background={`url(${config.imgPath}/d/46272/1682095879-white-texture-final.png)`}
          p="0"
        >
          {pageData.body?.map(section => {
            return (
              <RenderSections
                pageData={pageData}
                section={section}
                region={region}
                lang={lang}
                key={nanoid(4)}
              />
            );
          })}
        </Box>
      </ChakraProvider>
      <PageConfig isLikeVisible={false} pageData={pageData} />
    </WrapLayout>
  );
};

export const getStaticPaths = async () => {
  const lbPaths = config.USE_LOCAL_URL
    ? [
        `/linga-bhairavi/in/en/sadhana/bhairavi-sadhana-arpanam`,
        `/linga-bhairavi/np/en/sadhana/bhairavi-sadhana-arpanam`,
        `/linga-bhairavi/global/en/sadhana/bhairavi-sadhana-arpanam`,
        `/linga-bhairavi/np/ne/sadhana/bhairavi-sadhana-arpanam`,
        `/linga-bhairavi/in/en/sadhana/devi-dandam`,
        `/linga-bhairavi/in/en/sadhana/upasaka-training`,
        `/linga-bhairavi/in/en/sadhana/navratri-sadhana`,
        `/linga-bhairavi/in/en/sadhana/achala-arpanam`,
        `/linga-bhairavi/in/en/sadhana/linga-bhairavi-arati`,
        `/linga-bhairavi/in/en/sadhana/linga-bhairavi-stuti`,
      ]
    : await getLandingPaths('L2', '/sadhana/');
  return {
    paths: lbPaths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { region, lang, sadhana },
}) => {
  const topMenuData = await fetchHeaderMenuData(
    getLocale(region, lang, 'lb'),
    'lbtopmenu'
  );
  const headerMenuData = await fetchHeaderMenuData(
    getLocale(region, lang, 'lb'),
    'lbmainmenu'
  );
  const footerData = await fetchFooterDetailData(
    getLocale(region, lang, 'lb'),
    'lbfooter'
  );
  const pageData = await fetchDetails(region, lang, `sadhana/${sadhana}`, {
    preview,
    data: previewData,
  });

  return {
    props: {
      headerMenuData: headerMenuData?.data || null,
      footerData: footerData?.data || null,
      topMenuData: topMenuData,
      pageData: pageData || null,
      isPreview: preview ? true : false,
      region: region,
      lang: lang,
      menuType: 'lb',
    },
  };
};

export default Events;
